import { useState, useEffect } from 'react'
import type { Link } from '../types'
import { Input } from './ui/Input'
import { Textarea } from './ui/Textarea'
import { Select } from './ui/Select'
import { Button } from './ui/Button'
import { linkSchema } from '../utils/validation'

interface LinkFormProps {
  link: Link
  onChange: (link: Link) => void
  onSave: () => void
  onCancel: () => void
}

export function LinkForm({ link, onChange, onSave, onCancel }: LinkFormProps) {
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Validate on change
  useEffect(() => {
    const result = linkSchema.safeParse(link)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message
        }
      })
      setErrors(fieldErrors)
    } else {
      setErrors({})
    }
  }, [link])

  const hasErrors = Object.keys(errors).length > 0

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-title font-semibold mb-1.5 text-rhs-black">
          Label *
        </label>
        <Input
          value={link.label}
          onChange={(e) => onChange({ ...link, label: e.target.value })}
          error={errors.label}
          placeholder="Roosevelt High School"
        />
      </div>

      <div>
        <label className="block text-sm font-title font-semibold mb-1.5 text-rhs-black">
          URL *
        </label>
        <Input
          type="url"
          value={link.href}
          onChange={(e) => onChange({ ...link, href: e.target.value })}
          error={errors.href}
          placeholder="https://example.com"
        />
      </div>

      <div>
        <label className="block text-sm font-title font-semibold mb-1.5 text-rhs-black">
          Description
        </label>
        <Textarea
          value={link.desc || ''}
          onChange={(e) => onChange({ ...link, desc: e.target.value || undefined })}
          error={errors.desc}
          placeholder="Optional description"
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-title font-semibold mb-1.5 text-rhs-black">
          Badge
        </label>
        <Select
          value={link.badge || ''}
          onChange={(e) =>
            onChange({ ...link, badge: (e.target.value as Link['badge']) || undefined })
          }
        >
          <option value="">None</option>
          <option value="official">⭐ Official</option>
          <option value="video">🎥 Video</option>
          <option value="pdf">📄 PDF</option>
          <option value="internal">🔒 Internal</option>
          <option value="merch">🛍️ Merch</option>
          <option value="instagram">📸 Instagram</option>
          <option value="photos">🖼️ Photos</option>
        </Select>
      </div>

      <div className="flex gap-3 pt-4 border-t border-coolgray">
        <Button onClick={onSave} disabled={hasErrors} className="flex-1">
          Save
        </Button>
        <Button onClick={onCancel} variant="secondary" className="flex-1">
          Cancel
        </Button>
      </div>
    </div>
  )
}
